import { TableConfiguration, TableData } from "../types";
import {
    FV_LOOPS_ENDPOINT,
    PEAK_VALUES_ENDPOINT,
    POST_SPIROMETRY_ENDPOINT,
    PRE_SPIROMETRY_ENDPOINT,
    RESPONSES_ENDPOINT,
    SLOPES_ENDPOINT,
} from "./endpoints";
import {
    getRestBloodTestData,
    getExerciseBloodTestData,
} from "./forms/blood-test";
import { DynamicLoopsFromAPI } from "./forms/fv-loops/types";
import {
    PostSpirometryDataFromAPI,
    PostSpirometryParameterFromAPI,
    PreSpirometryDataFromAPI,
    PreSpirometryParameterFromAPI,
} from "./forms/spirometry/types";
import {
    BasicTableData,
    PeakValues,
    Responses,
    ResponseTableData,
    Slopes,
} from "./types";
import { fetcher } from "./utils";

export const getPeakValues = async ({ testId }: { testId: string }) => {
    const data = (await fetcher(PEAK_VALUES_ENDPOINT, {
        params: { test_id: testId },
    })) as PeakValues | null;

    if (!data) return null;

    const metabolicTableData = [
        data.time,
        data.load,
        data.vco2,
        data.vo2,
        data.vo2_kg,
        data.vat_prct_pred_vo2_max,
        data.vat_prct_vo2_max,
        data.vat,
        data.rer,
        data.mets,
    ];

    const GasExchangeTableData = [data.vd_vt, data.petco2, data.peto2];

    const cardiacTableData = [
        data.hr,
        data.o2pulse,
        data.hrr,
        data.sbp,
        data.dbp,
    ];

    const ventilatoryTableData = [
        data.ve,
        data.vt,
        data.bf,
        data.o2sat,
        data.ve_vo2,
        data.ve_vco2,
        data.br,
        data.ic,
    ];

    return [
        [
            {
                title: "Metabolic",
                data: convertPeakValuesData(metabolicTableData),
                configuration: TableConfiguration.PEAK_VALUES,
            },
            {
                title: "Gas Exchange",
                data: convertPeakValuesData(GasExchangeTableData),
                configuration: TableConfiguration.PEAK_VALUES,
            },
        ],
        [
            {
                title: "Cardiac",
                data: convertPeakValuesData(cardiacTableData),
                configuration: TableConfiguration.DEFAULT,
            },
            {
                title: "Ventilatory",
                data: convertPeakValuesData(ventilatoryTableData),
                configuration: TableConfiguration.DEFAULT,
            },
        ],
    ];
};

export const getSlopesAndResponses = async ({ testId }: { testId: string }) => {
    const slopesData = (await fetcher(SLOPES_ENDPOINT, {
        params: { test_id: testId },
    })) as Slopes | null;

    const responsesData = (await fetcher(RESPONSES_ENDPOINT, {
        params: { test_id: testId },
    })) as Responses | null;

    if (!slopesData && !responsesData) return null;

    const slopeTableData = !slopesData
        ? []
        : [
              slopesData.vo2_wr,
              slopesData.o2pulse,
              slopesData.ve_vco2,
              slopesData.petco2,
              slopesData.oues,
          ];

    const responsesTableData = !responsesData
        ? []
        : [
              responsesData.vo2_kg,
              responsesData.hr,
              responsesData.o2pulse,
              responsesData.ve,
              responsesData.vt,
              responsesData.bf,
              responsesData.ve_vo2,
              responsesData.ve_vco2,
              responsesData.petco2,
              responsesData.peto2,
              responsesData.vd_vt,
          ];

    return [
        [
            {
                title: "Slopes",
                data: convertPeakValuesData(slopeTableData),
                configuration: TableConfiguration.SLOPES,
            },
        ],
        [
            {
                title: "Responses",
                data: convertResponsesData(responsesTableData),
                configuration: TableConfiguration.RESPONSES,
            },
        ],
    ];
};

export const getSpirometeryData = async ({ testId }: { testId: string }) => {
    const preSpirometryData = (await fetcher(PRE_SPIROMETRY_ENDPOINT, {
        params: { test_id: testId },
    })) as PreSpirometryDataFromAPI | null;

    const postSpirometryData = (await fetcher(POST_SPIROMETRY_ENDPOINT, {
        params: { test_id: testId },
    })) as PostSpirometryDataFromAPI | null;

    const dynamicLoops = (await fetcher(FV_LOOPS_ENDPOINT, {
        params: { test_id: testId },
    })) as DynamicLoopsFromAPI | null;

    if (!preSpirometryData && !postSpirometryData && !dynamicLoops) return null;

    const preSpirometryTableData = !preSpirometryData
        ? []
        : [
              preSpirometryData.fvc,
              preSpirometryData.fev1,
              preSpirometryData.fev1_fvc,
              preSpirometryData.fef_25_75,
              preSpirometryData.mvv,
          ];

    const postSpirometryTableData = !postSpirometryData
        ? []
        : [
              postSpirometryData.fvc,
              postSpirometryData.fev1,
              postSpirometryData.fev1_fvc,
              postSpirometryData.fef_25_75,
          ];

    const dynamicLoopsTableData = !dynamicLoops
        ? []
        : [dynamicLoops.volume_limitation, dynamicLoops.flow_limitation];

    return [
        [
            {
                title: "Pre CPET Spirometry",
                data: convertPreSpirometryData(preSpirometryTableData),
                configuration: TableConfiguration.PRE_SPIROMETRY,
            },
            {
                title: "Dynamic F/V Loops",
                data: convertDynamicLoopsData(dynamicLoopsTableData),
                configuration: TableConfiguration.DYNAMIC_LOOP,
            },
        ],
        [
            {
                title: "Post CPET Spirometry",
                data: convertPostSpirometryData(postSpirometryTableData),
                configuration: TableConfiguration.POST_SPIROMETRY,
            },
        ],
    ];
};

export const getBloodTestData_forTable = async ({
    testId,
}: {
    testId: string;
}) => {
    const [restData, exerciseData] = await Promise.all([
        getRestBloodTestData({ testId }),
        getExerciseBloodTestData({ testId }),
    ]);

    if (!restData && !exerciseData) return null;

    const restBloodTestTableData: Array<TableData> = !restData
        ? []
        : Object.entries(restData).map(([, value]) => {
              return {
                  parameterName: value.label,
                  measured: value.measured ?? "-",
                  predicted: value.predicted ?? "-",
                  status: value.status,
                  statusColor: value.status_color,
              };
          });

    const exerciseBloodTestTableData: Array<TableData> = !exerciseData
        ? []
        : Object.entries(exerciseData).map(([, value]) => {
              return {
                  parameterName: value.label,
                  measured: value.measured ?? "-",
                  predicted: value.predicted ?? "-",
                  status: value.status,
                  statusColor: value.status_color,
              };
          });

    return [
        [
            {
                title: "Rest Lab Results",
                data: restBloodTestTableData,
                configuration: TableConfiguration.LAB_RESULTS,
            },
        ],
        [
            {
                title: "Peak Exercise Lab Results",
                data: exerciseBloodTestTableData,
                configuration: TableConfiguration.LAB_RESULTS,
            },
        ],
    ];
};

export const convertPeakValuesData = (data: BasicTableData[]) => {
    return data.map(
        (value) =>
            ({
                parameterName: value?.label ?? null,
                measured: value?.measured ?? null,
                predicted: value?.predicted_range ?? null,
                precentOfPredicted: value?.percent_of_predicted ?? null,
                status: value?.status ?? null,
                statusColor: value?.status_color ?? null,
            } as TableData)
    );
};

export const convertResponsesData = (data: ResponseTableData[]) => {
    return data.map(
        (value) =>
            ({
                parameterName: value.label,
                status: value.status,
                statusColor: value.status_color,
            } as TableData)
    );
};

export const convertPreSpirometryData = (
    data: PreSpirometryParameterFromAPI[]
) => {
    return data.map(
        (value) =>
            ({
                parameterName: value?.label,
                measured: value.measured ?? null,
                predicted: value.predicted_range ?? null,
                precentOfPredicted: value.percent_of_predicted ?? "-",
                status: value.status,
                statusColor: value.status_color,
            } as TableData)
    );
};

export const convertPostSpirometryData = (
    data: PostSpirometryParameterFromAPI[]
) => {
    return data.map(
        (value) =>
            ({
                parameterName: value?.label ?? null,
                measured: value.measured ?? null,
                prePostPrecent: value.pre_post_percent ?? "-",
                threshold: value.threshold ?? "-",
                status: value.status,
                statusColor: value.status_color,
            } as TableData)
    );
};

export const convertDynamicLoopsData = (
    data: PostSpirometryParameterFromAPI[]
) => {
    return data.map(
        (value) =>
            ({
                parameterName: value?.label ?? null,
                measured: value.measured ?? "-",
                threshold: value.threshold ?? null,
                status: value.status,
                statusColor: value.status_color,
            } as TableData)
    );
};
